import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function ScheduledAlertsList({ refreshInterval = 60000 }) {
  const [scheduledAlerts, setScheduledAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchScheduledAlerts();
    
    // Set up auto-refresh
    const intervalId = setInterval(() => {
      fetchScheduledAlerts();
      setNow(new Date());
    }, refreshInterval);
    
    // Update countdown timer every second
    const timerIntervalId = setInterval(() => {
      setNow(new Date());
    }, 1000);
    
    return () => {
      clearInterval(intervalId);
      clearInterval(timerIntervalId);
    };
  }, [refreshInterval]);

  const fetchScheduledAlerts = async () => {
    try {
      const response = await axios.get('/api/alerts/system/scheduled');
      setScheduledAlerts(response.data);
    } catch (error) {
      console.error('Error fetching scheduled alerts:', error);
      toast.error('Failed to load scheduled alerts');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (nextRunTime) => {
    const nextRun = new Date(nextRunTime);
    const diff = nextRun - now;
    
    if (diff <= 0) return 'Running now';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (loading && scheduledAlerts.length === 0) {
    return (
      <div className="bg-white shadow rounded p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded">
      <h2 className="bg-purple-l border-b border-gray-200 px-6 py-2 text-white font-bold tracking-wider uppercase text-md">
        Upcoming Executions
      </h2>
      
      <div className="p-4">
        {scheduledAlerts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {scheduledAlerts.map((alert, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-gray-800">{alert.alert_name}</div>
                    <div className="text-sm text-gray-500">
                      Next run: {new Date(alert.next_run_time).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      new Date(alert.next_run_time) - now < 5 * 60 * 1000 
                        ? 'text-red-600' 
                        : 'text-gray-600'
                    }`}>
                      {formatTimeRemaining(alert.next_run_time)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            No upcoming scheduled executions
          </div>
        )}
      </div>
    </div>
  );
} 